import { 
  useContext,
  useState, 
  useMemo, 
  useRef, 
  useEffect,
  useLayoutEffect
} from "react";

import { i18n } from "../../../../translate/i18n.js";

import { TenantContext } from '../../../../templates/context/TenantContext'

import { buildUnifiedRowGroups } from "../../business_rules/aggregation.js"
import { buildOtbVersionPayload } from "../../business_rules/otbPayloadBuilder"

import { ErrorModal, LoadingContent } from "../../../../components/SharedComponents/sharedComponents.index";
import { LOADING_CONTENT_TYPE } from "../../../../components/SharedComponents/LoadingContent/loading.content.constants"; 

import { useOtbCategoryEdits } from '../../hooks/useOtbCategoryEdits'
import { useOtbConsolidatedEdits } from '../../hooks/useOtbConsolidatedEdits'

import { OtbDraftRecoveryModal } from './components/OtbDraftRecoveryModal'
import { OtbSaveVersionModal } from './components/OtbSaveVersionModal'
import { OtbPurchasePlanModal } from './components/LinkToPurchasePlanSectionModal.jsx'
import { UnifiedConsolidadoTable } from './components/UnifiedConsolidadoTable'
import { OtbSelect } from "./components/OtbSelect.jsx";

import { SYSTEM_COLORS } from "../../../../components/system.colors.js";

const TOTALIZADORES_OPTIONS = [
  { key: "semestres", label: "Exibir semestres" },
  { key: "total", label: "Exibir total do ano" },
];

const VISUALIZACAO_OPTIONS = [
  { key: "variacoes", label: "Exibir variações" },
  { key: "estoqueReais", label: "Mostrar Estoque em R$" },
  // { key: "forecast", label: "Exibir forecast", chip: "IA" },
  { key: "custosPreco", label: "Exibir Todos os dados de Custos e de Preço" },
];

const normalizeStr = (value) => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

const buildProductHierarchy = ({
  plannedOtbConsolidated,
  openToBuyAchievedData,
  openToBuyByUnitsData,
  otbByValueData,
  categoryToGroupMap,
  selectedGroups
}) => {
  const filterRows = (rows) => (rows || []).filter(
    (row) => selectedGroups.includes(categoryToGroupMap?.[row.categoria])
  );

  return {
    plannedOtbConsolidated: filterRows(plannedOtbConsolidated),
    openToBuyAchievedData: filterRows(openToBuyAchievedData),
    openToBuyByUnitsData: filterRows(openToBuyByUnitsData),
    otbByValueData: filterRows(otbByValueData),
  };
};

const ChecklistDropdown = ({ actions, ctx, uiState }) => {
  const { onToggle, onReset } = actions || {};
  const { options = [], checkedKeys = [] } = ctx || {};
  const { label, sectionTitle } = uiState || {};

  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div ref={rootRef} className="otb-checklist-root">
      <button
        onClick={() => setOpen(o => !o)}
        className={`otb-checklist-trigger ${open ? "otb-checklist-trigger--open" : ""}`}>
        <span className="otb-checklist-trigger-label">
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="2" y1="5" x2="22" y2="5" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <line x1="2" y1="19" x2="22" y2="19" />
            <line x1="15" y1="2" x2="15" y2="8" />
            <line x1="8" y1="9" x2="8" y2="15" />
            <line x1="17" y1="16" x2="17" y2="22" />
          </svg>
          <span>{label}</span>
        </span>

        <span className="otb-checklist-trigger-spacer" />

        <span className="otb-checklist-trigger-right">
          {checkedKeys.length > 0 && (
            <span className="otb-checklist-badge">
              {checkedKeys.length}
            </span>
          )}
          <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2.5"
            className={`otb-checklist-chevron ${open ? "otb-checklist-chevron--open" : ""}`}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      {open && (
        <div className="otb-checklist-panel">
          <div className="otb-checklist-section-title">
            {sectionTitle}
          </div>

          {options.map(opt => {
            const checked = checkedKeys.includes(opt.key);
            return (
              <div
                key={opt.key}
                onClick={() => onToggle(opt.key)}
                className="otb-checklist-option">
                <span className={`otb-checklist-checkbox ${checked ? "otb-checklist-checkbox--checked" : ""}`}>
                  {checked && (
                    <svg width={9} height={9} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20,6 9,17 4,12" />
                    </svg>
                  )}
                </span>
                <span className="otb-checklist-option-label">{opt.label}</span>
                {opt.chip && (
                  <span className="otb-checklist-chip">
                    <svg width={9} height={9} viewBox="0 0 24 24" fill="none" stroke={"#e8773a"} 
                      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                      <path d="M20 3v4" /><path d="M22 5h-4" /><path d="M4 17v2" /><path d="M5 18H3" />
                    </svg>
                    {opt.chip}
                  </span>
                )}
              </div>
            );
          })}

          {onReset && (
            <div className="otb-checklist-reset-wrap">
              <button
                onClick={onReset}
                className="otb-checklist-reset-btn">
                REDEFINIR PADRÃO
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CollapseButton = ({ collapsed, onToggle }) => (
  <button onClick={onToggle} className="otb-collapse-btn">
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      className={`otb-collapse-chevron ${collapsed ? "otb-collapse-chevron--collapsed" : ""}`}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  </button>
);

export function OtbDetalhadoSection({ actions, ctx, uiState }) {

  const { userInfo } = useContext(TenantContext);
  const { userId, tenant } = userInfo || {};
  const { tenantId } = tenant || {};

  const [isEditing, setIsEditing] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isSavingVersion, setIsSavingVersion] = useState(false);
  const [saveVersionError, setSaveVersionError] = useState(null);
  const [showSaveOtbVersionError, setShowSaveOtbVersionError] = useState(false);
  const [saveOtbVersionErrorObj, setSaveOtbVersionErrorObj] = useState(null);
  const [consolidatedPurchasePlanTarget, setConsolidatedPurchasePlanTarget] = useState(null);
  const [purchasePlanTarget, setPurchasePlanTarget] = useState(null);


  const { 
    setSelectedCategory, 
    setSelectedCategoryName, 
    saveOtbVersion, 
    setSelectedCycleId,
    openPurchasePlan
  } = actions || {};

  const {
    otbByValueData,
    openToBuyByUnitsData,
    openToBuyAchievedData,
    otbDetailByLevel,
    categoryHierarchy,
    allOtbCycles,
    plannedOtbConsolidated,
    selectedOtb,
    selectedCategory,
    productHierarchy,
    categoryToGroupMap
  } = ctx || {};

  const { isLoadingOtbDetail } = uiState || {};
  
  useEffect(() => {
    if (document.getElementById('otb-row-flash-style')) return;
    const style = document.createElement("style");
    style.id = 'otb-row-flash-style';
    style.textContent = `
      @keyframes otbRowFlashFade {
        0%   { background-color: rgba(224,82,82,0.35); }
        100% { background-color: transparent; }
      }
      .otb-flash-cell { animation: otbRowFlashFade 1.4s ease-out; }

      @keyframes otbStickyFlashFade {
        0%   { background-color: #f4c2c2; }
        100% { background-color: #ffffff; }
      }
      .otb-flash-cell-sticky { animation: otbStickyFlashFade 1.4s ease-out; }
    `;
    document.head.appendChild(style);
  }, []);

  const [selectedGroups, setSelectedGroups] = useState([]);
  const [noGroupsSelected, setNoGroupsSelected] = useState(false);
  const [geralCollapsed, setGeralCollapsed] = useState(false);

  const [geralTotalizadores, setGeralTotalizadores] = useState([]);
  const [geralVisualizacao, setGeralVisualizacao] = useState([]);

  const [categoriaTotalizadores, setCategoriaTotalizadores] = useState([]);
  const [categoriaVisualizacao, setCategoriaVisualizacao] = useState([]);

  const generatedAt = new Date(selectedOtb?.createdAt).toLocaleString(i18n.language, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });

  const toggleInArray = (arr, setArr, key) => setArr(arr.includes(key) ? arr.filter(k => k !== key) : [...arr, key]);

  const groupOptions = productHierarchy?.values ?? [];
  const isAllGroupsSelected = !noGroupsSelected && (selectedGroups.length === 0 || selectedGroups.length === groupOptions.length);

  const onChangeGroups = (next) => {
    setNoGroupsSelected(next.length === 0);
    setSelectedGroups(next);
  };

  const cycleOptions = useMemo(
    () => (allOtbCycles || []).map((cycle) => ({ value: cycle.cycleId, label: cycle.otbName })),
    [allOtbCycles]
  );  

  const availableCategories = useMemo(() => {
    const normalizedSelectedGroups = selectedGroups.map(normalizeStr);
    const normalizedGroupMap = Object.fromEntries(
      Object.entries(categoryToGroupMap || {}).map(([category, group]) => [normalizeStr(category), normalizeStr(group)])
    );

    const keys = Object.keys(categoryToGroupMap || {})
      .filter(key => isAllGroupsSelected || normalizedSelectedGroups.includes(normalizedGroupMap[normalizeStr(key)]))
      .sort();

    return keys.map((key, index) => ({ label: key, value: index }));
  }, [categoryToGroupMap, selectedGroups, isAllGroupsSelected]);

  const effectiveValue = selectedCategory ?? availableCategories[0]?.value;

  const displayCategory = useMemo(
    () => availableCategories.find(opt => opt.value === effectiveValue)?.label ?? null,
    [availableCategories, effectiveValue]
  );

  const categoryTree = useMemo(() => {
    const availableLabels = new Set(availableCategories.map((option) => option.label));
    return {
      levels: categoryHierarchy?.levels ?? [],
      categories: (categoryHierarchy?.categories ?? []).filter((category) => availableLabels.has(category.categoria)),
    };
  }, [categoryHierarchy, availableCategories]);

  const categoryDisplayLevelIndexes = useMemo(() => {
    const levels = categoryHierarchy?.levels ?? [];
    const groupIndex = levels.findIndex((level) => level.levelName === productHierarchy?.levelName);
    return groupIndex >= 0 ? [groupIndex, levels.length - 1] : [levels.length - 1];
  }, [categoryHierarchy, productHierarchy]);  

  // novo
  const isCategoryLoading = isLoadingOtbDetail || (displayCategory !== null && otbDetailByLevel === undefined);

  const isFirstGroupsRender = useRef(true);

  useLayoutEffect(() => {
    if (isFirstGroupsRender.current) {
      isFirstGroupsRender.current = false;
      return;
    }
    setSelectedCategory?.(null);
  }, [selectedGroups, noGroupsSelected]);

  useEffect(() => {
    setSelectedGroups((prev) => (prev.length ? [] : prev));
    setNoGroupsSelected(false);
  }, [selectedOtb?.cycleId]);

  const geralData = useMemo(() => {
    const targetYear = new Date().getFullYear();
    const lastYear = targetYear - 1;

    const filterFromYear = (dataArray, year) =>
      (dataArray || []).filter(d => Number(d?.year) >= Number(year));

    const filterExactYear = (dataArray, year) =>
      (dataArray || []).filter(d => Number(d?.year) === Number(year));

    const source = isAllGroupsSelected
      ? {
          plannedOtbConsolidated,
          openToBuyAchievedData,
          openToBuyByUnitsData,
          otbByValueData,
        }
      : buildProductHierarchy({
          plannedOtbConsolidated,
          openToBuyAchievedData,
          openToBuyByUnitsData,
          otbByValueData,
          categoryToGroupMap,
          selectedGroups
        });

    return {
      // Plano: exibido a partir do ano atual; ano anterior separado para os deltas
      planoData: filterFromYear(source.plannedOtbConsolidated, targetYear),
      planoLastYearData: filterExactYear(source.plannedOtbConsolidated, lastYear),

      // Realizado Ano Atual e Ano Anterior
      realizadoData: filterExactYear(source.openToBuyAchievedData, targetYear),
      realizadoLastYearData: filterExactYear(source.openToBuyAchievedData, lastYear),

      // Estoque
      estoquePecasData: filterFromYear(source.openToBuyByUnitsData, targetYear),
      estoqueReaisData: filterFromYear(source.otbByValueData, targetYear),
    };
  }, [
    isAllGroupsSelected,
    selectedGroups,
    plannedOtbConsolidated,
    openToBuyAchievedData,
    openToBuyByUnitsData,
    otbByValueData,
    // otbDetailByLevel,
    categoryToGroupMap,
  ]);

  const categoriaData = useMemo(() => {
    const baseYear = new Date().getFullYear();
    const lastYear = baseYear - 1;

    const filterFromYear = (dataArray, year) =>
      (dataArray || []).filter(d => Number(d?.year) >= Number(year));

    const filterExactYear = (dataArray, year) =>
      (dataArray || []).filter(d => Number(d?.year) === Number(year));

    const planned = otbDetailByLevel?.plannedOtbByCategory?.[displayCategory];
    const achieved = otbDetailByLevel?.achievedOtbByCategory?.[displayCategory];
    const invUnits = otbDetailByLevel?.inventoryByUnitsAndCategory?.[displayCategory];
    const invValue = otbDetailByLevel?.inventoryByValueAndCategory?.[displayCategory];
    const brand = otbDetailByLevel?.brandByCategory?.[displayCategory];

    return {
      // Plano: exibido a partir do ano atual; ano anterior separado para os deltas
      planoData: filterFromYear(planned, baseYear),
      planoLastYearData: filterExactYear(planned, lastYear),

      // Realizado Ano Atual e Ano Anterior
      realizadoData: filterFromYear(achieved, baseYear),
      realizadoLastYearData: filterExactYear(achieved, lastYear),

      // Estoque Peças - Ano Atual e Ano Anterior
      estoquePecasData: filterFromYear(invUnits, baseYear),
      estoquePecasLastYearData: filterExactYear(invUnits, lastYear),

      // Estoque Reais - Ano Atual e Ano Anterior
      estoqueReaisData: filterFromYear(invValue, baseYear),
      estoqueReaisLastYearData: filterExactYear(invValue, lastYear),

      brandData: filterFromYear(brand, baseYear),
    };
  }, [otbDetailByLevel, displayCategory]);

  const {
    effectiveData: categoriaEffectiveData,
    handleFieldEdit: handleOtbFieldEdit,
    resetCategoryEdits,
    resetAllEdits,
    editedByCategory,
    draftJustHydrated,
    acknowledgeDraft,
    editedAnchors
  } = useOtbCategoryEdits(categoriaData, displayCategory, {
    tenantId: tenantId,
    userId: userId,
    cycleId: selectedOtb?.cycleId,
  });

  const { effectiveData: geralEffectiveData } = useOtbConsolidatedEdits(geralData, editedByCategory);

  const geralVariacoes = geralVisualizacao.includes("variacoes");
  const geralEstoqueReais = geralVisualizacao.includes("estoqueReais");
  const geralForecast = geralVisualizacao.includes("forecast");
  const geralCustosPreco = geralVisualizacao.includes("custosPreco");

  const categoriaVariacoes = categoriaVisualizacao.includes("variacoes");
  const categoriaEstoqueReais = categoriaVisualizacao.includes("estoqueReais");
  const categoriaForecast = categoriaVisualizacao.includes("forecast");
  const categoriaCustosPreco = categoriaVisualizacao.includes("custosPreco");

  const geralTable = useMemo(
    () => buildUnifiedRowGroups({
      ...geralEffectiveData,
      visualizacao: {
        variacoes: geralVariacoes,
        estoqueReais: geralEstoqueReais,
        forecast: geralForecast,
        custosPreco: geralCustosPreco,
      },
    }),
    [geralEffectiveData, geralVariacoes, geralEstoqueReais, geralForecast, geralCustosPreco]
  );

  const categoriaTable = useMemo(
    () => buildUnifiedRowGroups({
      ...categoriaEffectiveData,
      brandData: categoriaData.brandData,
      visualizacao: {
        variacoes: categoriaVariacoes,
        estoqueReais: categoriaEstoqueReais,
        forecast: categoriaForecast,
        custosPreco: categoriaCustosPreco,
      },
    }),
    [
      categoriaEffectiveData, 
      categoriaVariacoes, 
      categoriaEstoqueReais, 
      categoriaData.brandData,  
      categoriaForecast, 
      categoriaCustosPreco
    ]
  );

  const hasAnyEdits = Object.keys(editedByCategory).length > 0;

  const showEditActions = isEditing || hasAnyEdits;

  const editActionDisabledStyle = hasAnyEdits
    ? undefined
    : {
        background: SYSTEM_COLORS.GRAY.COOL_GRAY,
        borderColor: SYSTEM_COLORS.GRAY.COOL_GRAY,
        boxShadow: "none",
        cursor: "not-allowed",
      };  

  useEffect(() => {
    if (draftJustHydrated) {
      setIsEditing(true);
    }
  }, [draftJustHydrated]);

  useEffect(() => {
    setIsEditing(false);
  }, [displayCategory]);

  const handleConfirmSaveVersion = (versionName) => {
    const { validEdits, incompleteEdits } = buildOtbVersionPayload(editedByCategory, editedAnchors);

    if (incompleteEdits.length > 0) return;
    if (!saveOtbVersion) return;

    setIsSavingVersion(true);

    saveOtbVersion(selectedOtb?.cycleId, { versionName, edits: validEdits })
      .then(() => {
        resetAllEdits();
        setIsSaveModalOpen(false);
        setIsEditing(false);
      })
      .catch((err) => {
        setSaveOtbVersionErrorObj(err);
        setShowSaveOtbVersionError(true);
        setIsSaveModalOpen(false);
      })
      .finally(() => {
        setIsSavingVersion(false);
      });
  };

  useEffect(() => {
    setSelectedCategoryName?.(displayCategory);
  }, [displayCategory]);  

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="otb-header-row">
        <div style={{ display: 'flex', alignItems: "center", gap: "1rem" }}>
          <div className="otb-title-group">
            <div style={{ display: "flex", flexDirection: "row", gap: "0.4rem" }}>
              <div className="otb-title-bar" />
              <h1 className="otb-title">{selectedOtb?.otbName}</h1>
            </div>
              <span style={{ fontSize: "0.75rem" }}>{`Gerado em: ${generatedAt}`}</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>

            <OtbSelect
              label={"DEPARTAMENTO"}
              options={groupOptions}
              value={noGroupsSelected ? [] : (selectedGroups.length ? selectedGroups : groupOptions)}
              onChange={onChangeGroups}
            />

            <OtbSelect
              label={"OTB"}
              options={cycleOptions}
              value={selectedOtb?.cycleId}
              onChange={setSelectedCycleId}
              multiple={false}
            />
          </div>
        </div>

        <div className="otb-controls-group">
          <ChecklistDropdown
            actions={{ onToggle: (key) => toggleInArray(geralTotalizadores, setGeralTotalizadores, key) }}
            ctx={{ options: TOTALIZADORES_OPTIONS, checkedKeys: geralTotalizadores }}
            uiState={{ label: "Totalizadores", sectionTitle: "COLUNAS DE AGREGAÇÃO" }}
          />
          <ChecklistDropdown
            actions={{ onToggle: (key) => toggleInArray(geralVisualizacao, setGeralVisualizacao, key), onReset: () => setGeralVisualizacao([]) }}
            ctx={{ options: VISUALIZACAO_OPTIONS, checkedKeys: geralVisualizacao }}
            uiState={{ label: "Visualização", sectionTitle: "CONFIGURAÇÕES DA TABELA" }}
          />
        </div>
      </div>

      <section className="otb-card">
        <div className="otb-card-header">
          <hgroup>
            <h6 className="otb-card-heading">
              {selectedOtb?.otbName} — Consolidado
            </h6>

            <p style={{ fontSize: "0.56rem", color: "#888" }}>
              {"Valores numéricos arredondados. Ex.: R$ 12,3388 é exibido como R$ 12,34."}
            </p>
            </hgroup>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <CollapseButton
              collapsed={geralCollapsed}
              onToggle={() => setGeralCollapsed(c => !c)}
            />
          </div>        
        </div>

        {!geralCollapsed && (
          <UnifiedConsolidadoTable
            actions={{ onOtbLinkClick: setConsolidatedPurchasePlanTarget }}
            ctx={geralTable}
            uiState={{
              showSemestres: geralTotalizadores.includes("semestres"),
              showTotal: geralTotalizadores.includes("total"),
            }}
          />
        )}
      </section>

      <div>
        <div className="otb-detail-header-row">
          <div className="otb-detail-header-left">
            <div className="otb-detail-title-group">
              <div className="otb-detail-title-bar" />
              <span className="otb-detail-title">Detalhe por Categoria</span>
            </div>

            <OtbSelect
              label={"CATEGORIA"}
              title={"PRODUTO"}
              tree={categoryTree}
              value={displayCategory}
              displayLevelIndexes={categoryDisplayLevelIndexes}
              onChange={(categoria) =>
                setSelectedCategory?.(availableCategories.find((option) => option.label === categoria)?.value ?? null)
              }
            />
          </div>

          <div className="otb-controls-group">
            <ChecklistDropdown
              actions={{ onToggle: (key) => toggleInArray(categoriaTotalizadores, setCategoriaTotalizadores, key) }}
              ctx={{ options: TOTALIZADORES_OPTIONS, checkedKeys: categoriaTotalizadores }}
              uiState={{ label: "Totalizadores", sectionTitle: "COLUNAS DE AGREGAÇÃO" }}
            />
            <ChecklistDropdown
              actions={{ onToggle: (key) => toggleInArray(categoriaVisualizacao, setCategoriaVisualizacao, key), onReset: () => setCategoriaVisualizacao([]) }}
              ctx={{ options: VISUALIZACAO_OPTIONS, checkedKeys: categoriaVisualizacao }}
              uiState={{ label: "Visualização", sectionTitle: "CONFIGURAÇÕES DA TABELA" }}
            />
          </div>
        </div>

        <section className="otb-category-card">
          <div className="otb-category-header">
            <div className="otb-category-name-group">
              <span className="otb-category-name">{displayCategory}</span>
              <span className="otb-editable-badge">
                Editável
              </span>
            </div>

            <div className="otb-controls-group">
              {showEditActions && (
                <>
                  <button
                    className="otb-edit-btn otb-edit-btn--save"
                    disabled={!hasAnyEdits}
                    style={editActionDisabledStyle}
                    onClick={() => setIsSaveModalOpen(true)}
                  >
                    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={"#fff"}
                      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                      <polyline points="17 21 17 13 7 13 7 21" />
                      <polyline points="7 3 7 8 15 8" />
                    </svg>
                    Salvar versão
                  </button>

                  <button
                    className="otb-edit-btn otb-edit-btn--discard"
                    disabled={!hasAnyEdits}
                    style={editActionDisabledStyle}
                    onClick={() => {
                      resetAllEdits();
                      setIsEditing(false);
                    }}
                  >
                    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={"#fff"}
                      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                      <path d="M10 11v6" /><path d="M14 11v6" />
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                    </svg>
                    Descartar versão
                  </button>
                </>
              )}

              {!isEditing ? (
                <button className="otb-edit-btn" onClick={() => setIsEditing(true)}>
                  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={"#fff"}
                    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z" />
                  </svg>
                  Editar
                </button>
              ) : (
                <button className="otb-edit-btn otb-edit-btn--exit" onClick={() => setIsEditing(false)}>
                  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={"#fff"}
                    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                  Sair da edição
                </button>
              )}
            </div>
          </div>

          {isCategoryLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 100, padding: '32px 0' }}>
              <LoadingContent
                label={"Carregando categoria..."}
                mode={LOADING_CONTENT_TYPE.VIEW_DATA}
                type={LOADING_CONTENT_TYPE.VIEW_DATA}
              />
            </div>
          ) : (
            <UnifiedConsolidadoTable
              actions={{ onEditField: handleOtbFieldEdit, onOtbLinkClick: setPurchasePlanTarget }}
              ctx={categoriaTable}
              uiState={{
                showSemestres: categoriaTotalizadores.includes("semestres"),
                showTotal: categoriaTotalizadores.includes("total"),
                isEditing
              }}
            />
          )}          

          {draftJustHydrated && (
            <OtbDraftRecoveryModal
              actions={{
                onContinue: acknowledgeDraft,
                onDiscardCategory: () => {
                  resetCategoryEdits();
                  acknowledgeDraft();
                },
                onDiscardAll: () => {
                  resetAllEdits();
                  acknowledgeDraft();
                },
              }}
              ctx={{
                otbName: selectedOtb?.otbName,
                categories: Object.keys(editedByCategory),
                currentCategory: displayCategory,
              }}
            />
          )}

          {isSaveModalOpen && (
            <OtbSaveVersionModal
              actions={{
                onConfirm: handleConfirmSaveVersion,
                onCancel: () => {
                  setIsSaveModalOpen(false);
                  setSaveVersionError(null);
                },
              }}
              ctx={{
                otbName: selectedOtb?.otbName,
                categoriesCount: Object.keys(editedByCategory).length,
                incompleteEdits: buildOtbVersionPayload(editedByCategory, editedAnchors).incompleteEdits,
                isSaving: isSavingVersion,
              }}
            />
          )}

          <OtbPurchasePlanModal
            actions={{
              onClose: () => setPurchasePlanTarget(null),
              onConfirm: () => {
                openPurchasePlan?.({
                  cycleId: selectedOtb?.cycleId,
                  category: displayCategory,
                  monthKey: purchasePlanTarget?.monthKey,
                });
                setPurchasePlanTarget(null);
              },
            }}
            ctx={{
              collectionLabel: selectedOtb?.otbName,
              periodLabel: purchasePlanTarget?.month,
              categoryLabel: displayCategory,
              units: Math.round(purchasePlanTarget?.units ?? 0).toLocaleString(i18n.language),
            }}
            uiState={{ isOpen: purchasePlanTarget != null }}
          />

          <OtbPurchasePlanModal
            actions={{
              onClose: () => setConsolidatedPurchasePlanTarget(null),
              onConfirm: () => {
                openPurchasePlan?.({
                  cycleId: selectedOtb?.cycleId,
                  monthKey: consolidatedPurchasePlanTarget?.monthKey,
                  onlyWithPurchase: true,
                });
                setConsolidatedPurchasePlanTarget(null);
              },
            }}
            ctx={{
              collectionLabel: selectedOtb?.otbName,
              periodLabel: consolidatedPurchasePlanTarget?.month,
              categoryLabel: "Todas as categorias com abertura de compra",
              units: Math.round(consolidatedPurchasePlanTarget?.units ?? 0).toLocaleString(i18n.language),
            }}
            uiState={{ isOpen: consolidatedPurchasePlanTarget != null }}
          />

          {showSaveOtbVersionError && (
            <ErrorModal
              actions={{ onClose: () => setShowSaveOtbVersionError(false) }}
              ctx={{
                page: 'otb',
                operationType: 'save_otb_version',
                errorStatus: saveOtbVersionErrorObj?.response?.status || 500,
              }}
              uiState={{ isOpen: showSaveOtbVersionError }}
            />
          )}          
        </section>
      </div>
    </div>
  );
};